import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, rupees } from "@/lib/api";
import Header from "@/components/Header";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Package, CheckCircle2, Truck, Home, Circle, ShieldAlert, FileCheck2, FileX2, Clock } from "lucide-react";

const STATUS_STEPS = [
  { key: "confirmed", label: "Confirmed", icon: CheckCircle2 },
  { key: "packed", label: "Packed", icon: Package },
  { key: "dispatched", label: "Out for delivery", icon: Truck },
  { key: "delivered", label: "Delivered", icon: Home },
];

const StatusBadge = ({ status }) => (
  <Badge className={`status-${status} rounded-sm capitalize border-0`}>{status}</Badge>
);

const RxBadge = ({ status }) => {
  if (!status) return null;
  const map = {
    pending: { icon: Clock, cls: "status-packed", label: "Prescription: Awaiting review" },
    approved: { icon: FileCheck2, cls: "status-delivered", label: "Prescription: Approved" },
    rejected: { icon: FileX2, cls: "status-cancelled", label: "Prescription: Rejected" },
  };
  const cfg = map[status];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.cls} rounded-sm border-0 gap-1`} data-testid={`rx-badge-${status}`}>
      <Icon className="w-3 h-3"/> {cfg.label}
    </Badge>
  );
};

export function OrdersList() {
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/orders").then(({ data }) => setOrders(data)); }, []);

  return (
    <div className="min-h-screen bg-[#EFE7D6]">
      <Header />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        <div className="label-caps text-stone-500">History</div>
        <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1A362B] tracking-tight mt-2 mb-10">My Orders</h1>
        {orders === null ? (
          <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-28 rounded-md border border-stone-200 bg-white animate-pulse"/>)}</div>
        ) : orders.length === 0 ? (
          <div className="border border-dashed border-stone-300 rounded-md p-12 text-center">
            <p className="font-serif-display text-2xl text-[#1A362B]">No orders yet</p>
            <p className="text-stone-500 text-sm mt-2">Your future orders will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <Link key={o.id} to={`/orders/${o.id}`} className="block p-5 border border-stone-200 rounded-md bg-white hover:border-[#1A362B] transition-colors" data-testid={`order-row-${o.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono-num text-sm text-stone-600">#{o.order_no}</span>
                      <StatusBadge status={o.status}/>
                      <RxBadge status={o.prescription_status}/>
                    </div>
                    <div className="mt-2 text-sm text-stone-700">
                      {o.items.length} item{o.items.length > 1 ? "s" : ""} · {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-num text-xl text-[#1A362B]">{rupees(o.total)}</div>
                    <div className="text-xs text-stone-500 uppercase tracking-wider">{o.payment_method === "cod" ? "Cash on Delivery" : "Razorpay"}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function OrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [rxUrl, setRxUrl] = useState(null);
  useEffect(() => { api.get(`/orders/${id}`).then(({ data }) => setO(data)); }, [id]);

  // Load prescription as blob (auth-protected file)
  useEffect(() => {
    if (!o?.prescription_path) return;
    let objUrl;
    (async () => {
      try {
        const r = await api.get(`/prescriptions/file/${o.prescription_path}`, { responseType: "blob" });
        objUrl = URL.createObjectURL(r.data);
        setRxUrl(objUrl);
      } catch (err) {
        console.debug("Prescription preview load failed:", err?.message);
      }
    })();
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [o?.prescription_path]);

  if (!o) return (
    <div className="min-h-screen bg-[#EFE7D6]">
      <Header/>
      <div className="max-w-4xl mx-auto px-6 py-20 text-center text-stone-500">Loading…</div>
    </div>
  );

  const currentStepIdx = STATUS_STEPS.findIndex((s) => s.key === o.status);
  const isCancelled = o.status === "cancelled";

  return (
    <div className="min-h-screen bg-[#EFE7D6]">
      <Header/>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/orders" className="label-caps text-xs text-stone-600 hover:text-[#1A362B]" data-testid="back-orders">← Back to orders</Link>
        <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="label-caps text-stone-500">Order</div>
            <h1 className="font-serif-display text-4xl text-[#1A362B] tracking-tight mt-1">#{o.order_no}</h1>
            <div className="text-sm text-stone-500 mt-2">Placed on {new Date(o.created_at).toLocaleString("en-IN")}</div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <StatusBadge status={o.status}/>
            <RxBadge status={o.prescription_status}/>
          </div>
        </div>

        {o.prescription_status === "rejected" && (
          <div className="mt-6 p-4 rounded-md border-l-4 border-[#C44949] bg-[#C44949]/10">
            <div className="flex items-start gap-2">
              <FileX2 className="w-5 h-5 text-[#C44949] mt-0.5"/>
              <div className="text-sm">
                <div className="font-semibold text-[#8a2b2b]">Prescription rejected</div>
                {o.prescription_note && <div className="text-stone-700 mt-1">Reason: {o.prescription_note}</div>}
                <div className="text-stone-600 mt-1">Please contact us to re-upload a valid prescription.</div>
              </div>
            </div>
          </div>
        )}

        {!isCancelled && (
          <div className="mt-10 p-6 border border-stone-200 rounded-md bg-white">
            <div className="label-caps text-xs text-stone-500 mb-6">Tracking</div>
            <div className="grid grid-cols-4 gap-2">
              {STATUS_STEPS.map((s, i) => {
                const done = i <= currentStepIdx;
                const Icon = s.icon;
                return (
                  <div key={s.key} className="text-center">
                    <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center border-2 ${done ? "bg-[#1A362B] border-[#1A362B] text-[#EFE7D6]" : "bg-white border-stone-300 text-stone-400"}`}>
                      <Icon className="w-4 h-4"/>
                    </div>
                    <div className={`mt-2 label-caps text-[10px] ${done ? "text-[#1A362B]" : "text-stone-400"}`}>{s.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6 mt-10">
          <div className="md:col-span-2 p-6 border border-stone-200 rounded-md bg-white">
            <div className="label-caps text-xs text-stone-500 mb-4">Items</div>
            <div className="space-y-4">
              {o.items.map((it) => (
                <div key={it.medicine_id} className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#FFFFFF] rounded-sm overflow-hidden shrink-0">
                    <img src={it.image_url} alt="" className="w-full h-full object-cover"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#1A362B] truncate">{it.name}</div>
                    <div className="text-xs text-stone-500">Qty {it.quantity} × {rupees(it.price)}</div>
                  </div>
                  <div className="font-mono-num text-sm">{rupees(it.price * it.quantity)}</div>
                </div>
              ))}
            </div>
            <Separator className="my-6"/>
            <div className="space-y-2 font-mono-num text-sm">
              <div className="flex justify-between"><span className="text-stone-600">Subtotal</span><span>{rupees(o.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-stone-600">Delivery</span><span>{o.delivery_fee === 0 ? "Free" : rupees(o.delivery_fee)}</span></div>
              <div className="flex justify-between text-lg font-serif-display text-[#1A362B] pt-2 border-t border-stone-200"><span>Total</span><span className="font-mono-num">{rupees(o.total)}</span></div>
            </div>
          </div>
          <div className="space-y-6">
            <div className="p-6 border border-stone-200 rounded-md bg-white">
              <div className="label-caps text-xs text-stone-500 mb-3">Delivery to</div>
              <div className="text-sm text-[#1A362B]">
                <div>{o.address.line1}</div>
                {o.address.line2 && <div>{o.address.line2}</div>}
                <div>{o.address.city}, {o.address.state} - {o.address.pincode}</div>
                <div className="mt-2 text-stone-500">📞 {o.address.phone}</div>
              </div>
            </div>
            <div className="p-6 border border-stone-200 rounded-md bg-white">
              <div className="label-caps text-xs text-stone-500 mb-3">Payment</div>
              <div className="text-sm text-[#1A362B]">
                <div>{o.payment_method === "cod" ? "Cash on Delivery" : "Razorpay"}</div>
                <div className="text-xs text-stone-500 mt-1 uppercase tracking-wider">{o.payment_status}</div>
              </div>
            </div>
            {o.prescription_path && (
              <div className="p-6 border border-stone-200 rounded-md bg-white">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldAlert className="w-4 h-4 text-[#1A362B]"/>
                  <div className="label-caps text-xs text-stone-500">Your prescription</div>
                </div>
                {rxUrl ? (
                  <a href={rxUrl} target="_blank" rel="noreferrer" className="block">
                    <img src={rxUrl} alt="Prescription" className="w-full rounded-sm border border-stone-200 object-cover max-h-64"/>
                    <div className="text-xs text-stone-500 mt-2 hover:text-[#1A362B]">Tap to open full size</div>
                  </a>
                ) : (
                  <div className="h-24 rounded-sm border border-dashed border-stone-300 flex items-center justify-center text-xs text-stone-500">Loading…</div>
                )}
              </div>
            )}
            {o.timeline?.length > 0 && (
              <div className="p-6 border border-stone-200 rounded-md bg-white">
                <div className="label-caps text-xs text-stone-500 mb-3">History</div>
                <div className="space-y-3">
                  {o.timeline.map((t, i) => (
                    <div key={`${t.status}-${t.at}-${i}`} className="flex gap-3 text-xs">
                      <Circle className="w-2 h-2 mt-1 fill-[#1A362B] text-[#1A362B] shrink-0"/>
                      <div>
                        <div className="text-[#1A362B] capitalize">{t.status.replace(/-/g, " ")}</div>
                        <div className="text-stone-500">{new Date(t.at).toLocaleString("en-IN")}</div>
                        {t.note && <div className="text-stone-600 italic">{t.note}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
